import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationExtras, ParamMap, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {APP_REQUEST_STEP, APP_REQUESTS, PARAM_REQUEST_ID, PARAM_REQUEST_STEP_ID} from '../../config/app-urls';
import {RequestDetailViewModel} from '../view-models/request-detail.view-model';
import {RequestViewModel} from '../view-models/request.view-model';
import {RequestStatusEnum} from '@dto/requests/request-status.enum';
import {RequestStepDetailViewModel} from '../view-models/request-step-detail.view-model';
import {RequestStepAnchorDirective} from './request-step-anchor.directive';
import {RequestService} from '../services/request.service';
import {RequestConverterService} from '../services/request-converter.service';
import {RequestComponentMappingService} from './components/request-component-mapping.service';
import {Validator} from '@shared/utilities/validator';
import {RequestStepStateViewModel} from '../view-models/request-step-state.view-model';
import {RequestStepUpdateDto} from '@dto/requests/request-step-update.dto';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';

@Component({
    selector: 'app-request',
    templateUrl: './request.component.html',
    styleUrls: ['./request.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent, RequestStepAnchorDirective]
})
export class RequestComponent implements OnInit {

  public static readonly EXISTING_REQUEST_KEY = 'taskDetailViewModel';

  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly requestService = inject(RequestService);
  private readonly requestConverterService = inject(RequestConverterService);
  private readonly requestComponentMappingService = inject(RequestComponentMappingService);
  // ChangeDetectorRef needed only for renderStepComponent: forces synchronous render so ViewChild is available
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private outputSubscriptions: { unsubscribe(): void }[] = [];

  readonly dataLoading = signal(true);
  readonly stepProcessing = signal(false);
  readonly stepLoadError = signal(false);
  readonly request = signal<RequestDetailViewModel | undefined>(undefined);
  public currentStep: RequestStepDetailViewModel;
  public currentStepIndex: number;
  public maxEditableStepIndex = 0;

  @ViewChild(RequestStepAnchorDirective)
  private readonly requestDirective: RequestStepAnchorDirective;

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.stepProcessing.set(false);

    // Do not manage this ActivatedRoute subscription, per Angular documentation
    this.route.paramMap.subscribe((paramMap: ParamMap) => {
      const requestId = paramMap.get(PARAM_REQUEST_ID.name);
      const stepId = paramMap.get(PARAM_REQUEST_STEP_ID.name);

      this.loadRequestStep(requestId, stepId);
    });
  }

  private loadRequestStep(requestId: string, stepId: unknown) {
    if (Validator.isNullOrWhitespace(requestId)) {
      this.handleNotFound();
      return;
    }

    // Already loaded for this requestId — just navigate to the appropriate step
    const currentRequest = this.request();
    if (currentRequest?.id === requestId) {
      this.processRequestStep(requestId, stepId, currentRequest);
      return;
    }

    const stateValue = history.state[RequestComponent.EXISTING_REQUEST_KEY];

    // Full detail view model in state (e.g. internal step-to-step redirection passing full VM)
    if (this.isFullDetailViewModel(stateValue) && stateValue.id === requestId) {
      this.processRequestStep(requestId, stepId, stateValue);
      return;
    }

    // Summary only in state (from list or initiate navigation) — load steps from API
    if (stateValue?.id === requestId) {
      this.loadStepsAndMerge(requestId, stepId, stateValue as RequestViewModel);
      return;
    }

    // No summary available (deep link / bookmark / browser refresh without navigation state)
    // Load only the steps from API and construct a skeleton summary from the URL request ID.
    this.loadStepsWithSkeletonSummary(requestId, stepId);
  }

  private loadStepsAndMerge(requestId: string, stepId: unknown, summary: RequestViewModel): void {
    this.dataLoading.set(true);

    this.requestService.getRequest(requestId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: requestDetailDto => {
        const requestViewModel = this.requestConverterService.convertRequestDetailDtoToRequestDetailViewModel(summary, requestDetailDto);
        this.request.set(requestViewModel);
        this.processRequestStep(requestId, stepId, requestViewModel);
      },
      error: () => {
        this.dataLoading.set(false);
        this.handleNotFound();
      }
    });
  }

  private loadStepsWithSkeletonSummary(requestId: string, stepId: unknown): void {
    // Construct a minimal summary using only the known request ID.
    // Status defaults to in-progress (most common deep-link scenario).
    // Description and percentage are unavailable without the list endpoint,
    // but steps will render correctly for the user.
    const skeletonSummary: RequestViewModel = {
      id: requestId,
      status: RequestStatusEnum.InProgress,
      readonly: false,
      description: '',
      percentageComplete: 0,
      originalTemplateId: ''
    };
    this.loadStepsAndMerge(requestId, stepId, skeletonSummary);
  }

  private isFullDetailViewModel(value: unknown): value is RequestDetailViewModel {
    return (
      typeof value === 'object' &&
      value !== null &&
      typeof (value as RequestDetailViewModel).id === 'string' &&
      Array.isArray((value as RequestDetailViewModel).steps)
    );
  }

  private handleNotFound() {
    this.router.navigate(APP_REQUESTS.toRouterLinkCommands());
    // TECHDEBT: Switch to a real 404 page when Azure #7378 / Pivotal #178894411 is implemented
    // this.router.navigate(APP_404.toRouterLinkCommands(), {skipLocationChange: true});
  }

  private processRequestStep(requestId: string, stepId: unknown, request: RequestDetailViewModel): void {
    // If a valid step ID was not provided, redirect to the first step
    if (stepId === null) {
      // To save on reloading the request, pass it through to ourselves and replace the current /requests/request/XYZ[/steps] URL
      const navigationExtras: NavigationExtras = {
        replaceUrl: true,
        state: {[RequestComponent.EXISTING_REQUEST_KEY]: request}
      };

      this.navigateToStep(requestId, request.steps[0].id, navigationExtras);
      return;
    }

    this.request.set(request);
    this.maxEditableStepIndex = this.findMaxEditableStepIndex(request.steps);

    const stepIdNumber = Number(stepId);
    const stepIndex = this.findRequestStepIndex(stepIdNumber);

    // If the step ID was not found OR is after the max editable step, current business rules are to redirect to the max editable step
    if (stepIndex === -1 || stepIndex > this.maxEditableStepIndex) {
      // Replace the invalid URL with the new one
      const navigationExtras: NavigationExtras = {
        replaceUrl: true,
      };
      this.navigateToStep(requestId, request.steps[this.maxEditableStepIndex].id, navigationExtras);
      return;
    }

    this.currentStep = request.steps[stepIndex];
    this.currentStepIndex = stepIndex;

    this.loadRequestStepDetailsAndRender(requestId, this.currentStep.id);
  }

  private navigateToStep(requestId: string, stepId: number, navigationExtras?: NavigationExtras) {
    const targetRouterLinkCommands = APP_REQUEST_STEP.toRouterLinkCommands(requestId, String(stepId));
    this.router.navigate(targetRouterLinkCommands, navigationExtras);
  }

  private findMaxEditableStepIndex(steps: RequestStepDetailViewModel[]): number {
    const result = steps.findIndex(requestStep => !requestStep.completed);

    // If there are no unsubmitted steps, then all the steps were already submitted and the max editable step is the last one
    if (result === -1) {
      return steps.length - 1;
    }

    return result;
  }

  private findRequestStepIndex(stepId: number): number {
    return this.request()?.steps.findIndex(requestStep => requestStep.id === stepId) ?? -1;
  }

  private loadRequestStepDetailsAndRender(requestId: string, stepId: number): void {
    this.dataLoading.set(true);
    this.stepLoadError.set(false);

    this.requestService.getRequestStep(requestId, stepId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: requestStepDetailDto => {
        const requestStep = this.requestConverterService.convertRequestStepDetailDtoToRequestStepDetailViewModel(requestStepDetailDto);
        this.replaceCurrentStep(requestStep);
        this.dataLoading.set(false);
        this.renderStepComponent();
      },
      error: () => {
        this.dataLoading.set(false);
        this.stepLoadError.set(true);
      }
    });
  }

  retryLoadStep(): void {
    const req = this.request();
    if (req && this.currentStep) {
      this.loadRequestStepDetailsAndRender(req.id, this.currentStep.id);
    }
  }

  private replaceCurrentStep(requestStep: RequestStepDetailViewModel): void {
    const current = this.request();
    if (!current) return;
    const updatedSteps = [...current.steps];
    updatedSteps[this.currentStepIndex] = requestStep;
    this.request.set({ ...current, steps: updatedSteps });
    this.currentStep = requestStep;
  }

  private renderStepComponent(): void {
    this.dataLoading.set(false);

    // Force synchronous render so the @if block updates and ViewChild (ng-template) becomes available
    this.changeDetectorRef.detectChanges();

    this.outputSubscriptions.forEach(s => s.unsubscribe());
    this.outputSubscriptions = [];

    const container = this.requestDirective.viewContainerRef;
    container.clear();

    const requestComponentType = this.requestComponentMappingService.map(this.currentStep.type);
    const requestComponentRef = container.createComponent(requestComponentType);
    const requestComponent = requestComponentRef.instance;

    const req = this.request();
    if (!req) return;
    const state = new RequestStepStateViewModel(this.currentStepIndex + 1, req.steps.length, req.readonly);
    requestComponent.initialize(req, this.currentStep, state);

    this.outputSubscriptions.push(
      requestComponent.previousClicked.subscribe(() => this.goToPreviousStep()),
      requestComponent.nextClicked.subscribe(() => this.goToNextStep()),
      requestComponent.closeClicked.subscribe(() => this.closeRequest()),
      requestComponent.requestUpdated.subscribe(updatedRequest => this.updateRequest(updatedRequest)),
      requestComponent.processingUpdated.subscribe(processing => this.stepProcessing.set(processing))
    );
  }

  private goToPreviousStep(): void {
    if (this.hasPrevious()) {
      this.goToStepIndex(this.currentStepIndex - 1);
    }
  }

  private goToNextStep(): void {
    if (this.hasNext()) {
      this.goToStepIndex(this.currentStepIndex + 1);
    } else {
      this.closeRequest();
    }
  }

  private hasPrevious() {
    return this.currentStepIndex > 0;
  }

  private hasNext() {
    return this.currentStepIndex < ((this.request()?.steps.length ?? 0) - 1);
  }

  public closeRequest(): void {
    const req = this.request();
    if (req) {
      this.router.navigate(req.closeRouterLinkCommands);
    }
  }

  public goToStepIndex(stepIndex: number): void {
    const req = this.request();
    if (req && stepIndex !== this.currentStepIndex && stepIndex <= this.maxEditableStepIndex) {
      const stepId = req.steps[stepIndex].id;
      this.navigateToStep(req.id, stepId);
    }
  }

  private updateRequest(updateDto: RequestStepUpdateDto) {
    const current = this.request();
    if (!current) return;

    const updatedStep = this.requestConverterService.convertRequestStepDetailDtoToRequestStepDetailViewModel(updateDto.step);
    const updatedSteps = [...current.steps];
    updatedSteps[this.currentStepIndex] = updatedStep;
    // Preserve summary from current signal — do NOT overwrite with updateDto.request fields
    const updatedRequest: RequestDetailViewModel = { ...current, steps: updatedSteps };
    this.request.set(updatedRequest);
    this.currentStep = updatedStep;
    this.maxEditableStepIndex = this.findMaxEditableStepIndex(updatedRequest.steps);
  }
}
